import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

// Decided synchronously on first render so the overlay is up before the
// welcome content can flash. Plays on every fresh load of the entry page.
// Skipped when the tab is hidden or the user prefers reduced motion.
function shouldPlay() {
  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    if (document.hidden) return false;
    return true;
  } catch {
    return false;
  }
}

export default function IntroLogoOverlay() {
  const [phase, setPhase] = useState(() => (shouldPlay() ? 'playing' : 'exit'));
  const [gone, setGone] = useState(() => !shouldPlay());
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (gone) return;
    if (phase === 'playing') {
      document.body.style.overflow = 'hidden';
      const t1 = setTimeout(() => setPhase('exit'), 3200);
      const t2 = setTimeout(() => setGone(true), 4700); // hard fail-safe: never trap the user
      const onKey = (e) => {
        if (e.key === 'Escape') setPhase('exit');
      };
      // If the tab was backgrounded mid-intro, timers may be throttled —
      // exit as soon as the user returns so the overlay can never hang.
      const onVisible = () => {
        if (!document.hidden) setPhase('exit');
      };
      window.addEventListener('keydown', onKey);
      document.addEventListener('visibilitychange', onVisible);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        window.removeEventListener('keydown', onKey);
        document.removeEventListener('visibilitychange', onVisible);
        document.body.style.overflow = '';
      };
    }
    if (phase === 'exit') {
      const t = setTimeout(() => setGone(true), 1400);
      return () => clearTimeout(t);
    }
  }, [phase, gone]);

  if (gone) return null;

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex cursor-pointer items-center justify-center bg-black"
      data-testid="intro-logo-overlay"
      role="presentation"
      onClick={() => setPhase('exit')}
      style={{ pointerEvents: phase === 'exit' ? 'none' : 'auto' }}
      initial={{ opacity: 1 }}
      animate={{ opacity: phase === 'exit' ? 0 : 1 }}
      transition={{ duration: phase === 'exit' ? 0.8 : 0, ease: 'easeOut' }}
      onAnimationComplete={() => {
        if (phase === 'exit') setGone(true);
      }}
    >
      <div className="relative flex h-full w-full items-center">
        {!imgFailed && (
          <motion.img
            src="/assets/hero/frame-rest.jpg"
            alt=""
            aria-hidden="true"
            draggable={false}
            className="absolute inset-0 h-full w-full object-cover object-[68%_20%] md:object-[62%_20%] lg:object-[50%_20%]"
            data-testid="intro-hero-image"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.2, ease: 'easeOut' }}
            onError={() => setImgFailed(true)}
          />
        )}
        {!imgFailed && (
          <div
            className="absolute inset-0 bg-gradient-to-r from-base via-base/75 to-base/10 lg:via-base/45"
            aria-hidden="true"
          />
        )}
        <div className="relative z-10 flex w-full px-8 sm:px-12 lg:px-24">
          <div className="flex flex-col items-center" data-testid="intro-lockup">
            <motion.img
              src="/assets/brand/rangesafe-logo.png"
              alt="RangeSafe"
              draggable={false}
              className="h-auto w-[300px] max-w-[70vw] sm:w-[380px] lg:w-[460px]"
              data-testid="intro-logo"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease: 'easeOut' }}
              onError={() => setPhase('exit')}
            />
            <motion.p
              className="mt-6 text-center font-display font-bold uppercase leading-none tracking-tight text-white"
              style={{ fontSize: 'clamp(1.4rem, 3.2vw, 2.75rem)' }}
              data-testid="intro-welcome-text"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8, ease: 'easeOut' }}
            >
              Welcome to RangeSafe
            </motion.p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
